import { Modal } from 'almanac-ui/Modal'
import { type ButtonProps } from 'blaze-ui/Button/Types'
import { Selection, useCreateRevision } from 'hooks/queries/layers/revisions/useCreateRevision'
import { TextSelection, Selection as ProseSelection } from 'prosemirror-state'
import { observer } from 'mobx-react-lite'
import { AlmanacEditorState } from 'stores/EditorState'
import { markdownSerializer } from 'common/Prose/components/TextInlineFormatBar/components/FormatBars/markdownSerializer'
import { RevisionPrompt } from 'types'
import { Button } from 'blaze-ui/Button'
import useResponsiveDesign from 'hooks/useResponsiveDesign'

interface Props extends Pick<ButtonProps, 'size' | 'variant'> {
  articleId: string
  layerId: string
  prompt: RevisionPrompt
  onRevise?: () => any
  almanacEditorState: AlmanacEditorState
  selection: Selection
  previousRevisionId?: string
  label?: string
}

export const ReviseButton = observer(
  ({
    articleId,
    layerId,
    prompt,
    onRevise,
    almanacEditorState,
    selection,
    previousRevisionId,
    label,
    size = 'lg',
    variant = 'primary',
  }: Props) => {
    const { isMobile } = useResponsiveDesign()
    const { mutate, isLoading } = useCreateRevision(
      { articleKey: articleId, layerId },
      {
        onSuccess: () => {
          const tr = almanacEditorState.view.state.tr
          tr.setSelection(TextSelection.create(almanacEditorState.view.state.doc, 0))
          almanacEditorState.view.dispatch(tr)
          onRevise?.()
        },
      },
    )

    const handleSubmit = () => {
      const doc = almanacEditorState.view.state.doc
      const proseSelection = ProseSelection.fromJSON(doc, selection)
      const documentAsMarkdown = markdownSerializer.serialize(doc)
      const selectionContent = doc.cut(proseSelection.from, proseSelection.to)
      const selectionAsMarkdown = markdownSerializer.serialize(selectionContent)

      mutate({ prompt, selection, selectionAsMarkdown, documentAsMarkdown, previousRevisionId })
    }

    if (isMobile)
      return (
        <Button fullWidth size="xl" onPress={handleSubmit} isDisabled={isLoading}>
          {label || 'Revise'}
        </Button>
      )

    return (
      <Modal.FooterButton size={size} variant={variant} onPress={handleSubmit} isDisabled={isLoading}>
        {label || 'Revise'}
      </Modal.FooterButton>
    )
  },
)
